import { useState, useMemo, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Gift, ShoppingCart, ChevronLeft, Check, RefreshCw } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';

const HAMPER_IMAGES = {
  'hamper-1': 'https://images.pexels.com/photos/28769885/pexels-photo-28769885.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  'hamper-2': 'https://images.unsplash.com/photo-1766375885762-41cfbacde261?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
  'hamper-3': 'https://images.unsplash.com/photo-1720788810305-85fe22e6c3a2?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
  'hamper-4': 'https://images.pexels.com/photos/17878238/pexels-photo-17878238.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  'hamper-5': 'https://images.unsplash.com/photo-1711645973621-fc4929ed9d5a?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
};

// editMode: bool — true when editing an existing cart hamper
// hamperCartId: string — ID of the cart item being edited
// initialSelections: { [groupId]: optionId } — pre-filled from existing cart item
export const GiftHamperDialog = ({ hamper, open, onClose, editMode = false, hamperCartId = null, initialSelections = {} }) => {
  const [selections, setSelections] = useState({});
  const [saving, setSaving] = useState(false);
  const { addHamperToCart, updateHamperInCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Sync selections whenever the dialog opens (or switches between items)
  useEffect(() => {
    if (open) {
      setSelections(editMode ? initialSelections : {});
    }
  }, [open, hamperCartId]);

  const totalPrice = useMemo(() => {
    if (!hamper) return 0;
    let price = hamper.base_price;
    for (const group of hamper.selection_groups) {
      const selectedOptionId = selections[group.id];
      if (selectedOptionId) {
        const opt = group.options.find(o => o.id === selectedOptionId);
        if (opt) price += opt.addon_price;
      }
    }
    return price;
  }, [hamper, selections]);

  const allSelected = hamper
    ? hamper.selection_groups.every(g => selections[g.id])
    : false;

  const handleSelect = (groupId, optionId) => {
    setSelections(prev => ({ ...prev, [groupId]: optionId }));
  };

  const handleSubmit = async () => {
    if (!user) {
      toast.error('Please login to continue');
      navigate('/login');
      return;
    }
    if (!allSelected) {
      toast.error('Please make all selections before continuing');
      return;
    }
    setSaving(true);
    try {
      const selectionDetails = hamper.selection_groups.map(group => {
        const opt = group.options.find(o => o.id === selections[group.id]);
        return {
          group_id: group.id,
          group_label: group.label,
          option_id: opt.id,
          option_name: opt.name,
          addon_price: opt.addon_price,
        };
      });
      const payload = {
        hamper_id: hamper.id,
        hamper_name: hamper.name,
        selections: selectionDetails,
        unit_price: totalPrice,
        quantity: 1,
      };

      if (editMode && hamperCartId) {
        await updateHamperInCart(hamperCartId, payload);
        toast.success('Hamper updated!');
      } else {
        await addHamperToCart(payload);
        toast.success(`${hamper.name} added to cart!`);
      }
      onClose();
    } catch (err) {
      toast.error(editMode ? 'Failed to update hamper.' : 'Failed to add to cart.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  if (!hamper) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0" data-testid="hamper-dialog">
        {/* Header image */}
        <div className="relative h-48 sm:h-56 overflow-hidden rounded-t-lg">
          <img
            src={HAMPER_IMAGES[hamper.id] || HAMPER_IMAGES['hamper-1']}
            alt={hamper.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
          <div className="absolute bottom-4 left-4 text-white">
            <h2 className="text-xl font-bold">{hamper.name}</h2>
            <p className="text-sm text-white/80">{hamper.tagline}</p>
          </div>
          <div className="absolute top-4 left-4 flex flex-col items-end gap-2">
            <Badge className="bg-primary text-white border-0 text-sm">
              Starts at ₹{hamper.base_price}
            </Badge>
            {editMode && (
              <Badge className="bg-amber-500 text-white border-0 text-xs">
                Editing
              </Badge>
            )}
          </div>
        </div>

        <div className="p-5 space-y-5">
          <DialogHeader>
            <DialogTitle className="sr-only">{hamper.name} — {editMode ? 'Edit' : 'Customize'} Your Hamper</DialogTitle>
          </DialogHeader>

          {/* Selection groups */}
          {hamper.selection_groups.map((group) => (
            <div key={group.id} data-testid={`selection-group-${group.id}`}>
              <div className="flex items-center gap-2 mb-3">
                <h3 className="font-semibold text-accent text-sm">{group.label}</h3>
                {selections[group.id] && (
                  <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-300 bg-emerald-50">
                    <Check className="h-3 w-3 mr-1" /> Selected
                  </Badge>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {group.options.map((option) => {
                  const isSelected = selections[group.id] === option.id;
                  return (
                    <button
                      key={option.id}
                      onClick={() => handleSelect(group.id, option.id)}
                      data-testid={`option-${group.id}-${option.id}`}
                      className={`flex items-center justify-between p-3 rounded-lg border-2 text-left transition-all ${
                        isSelected
                          ? 'border-primary bg-amber-50 shadow-sm'
                          : 'border-amber-100 hover:border-amber-300 hover:bg-amber-50/50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`h-4 w-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'border-primary bg-primary' : 'border-gray-300'
                        }`}>
                          {isSelected && <div className="h-1.5 w-1.5 rounded-full bg-white" />}
                        </div>
                        <span className={`text-sm font-medium ${isSelected ? 'text-accent' : 'text-muted-foreground'}`}>
                          {option.name}
                        </span>
                      </div>
                      {option.addon_price > 0 && (
                        <span className="text-xs font-semibold text-orange-600 ml-2 flex-shrink-0">
                          +₹{option.addon_price}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Price summary */}
          <div className={`rounded-xl p-4 border-2 transition-all ${
            allSelected ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-100'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  {allSelected ? 'Total Price' : 'Base Price'}
                </p>
                <p className="text-2xl font-bold text-primary" data-testid="hamper-total-price">
                  ₹{totalPrice}
                </p>
                {!allSelected && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Select all options to see final price
                  </p>
                )}
              </div>
              {allSelected && (
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Includes</p>
                  <div className="space-y-0.5 mt-1">
                    {hamper.selection_groups.map(g => {
                      const opt = g.options.find(o => o.id === selections[g.id]);
                      return opt ? (
                        <p key={g.id} className="text-xs font-medium text-accent">
                          {opt.name}
                        </p>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleClose}
              className="flex-1 border-amber-200"
              data-testid="hamper-back-button"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!allSelected || saving}
              className="flex-1 bg-primary hover:bg-primary/90 rounded-lg"
              data-testid={editMode ? 'hamper-update-button' : 'hamper-add-to-cart-button'}
            >
              {editMode ? (
                <>
                  <RefreshCw className={`h-4 w-4 mr-2 ${saving ? 'animate-spin' : ''}`} />
                  {saving ? 'Updating...' : 'Update Hamper'}
                </>
              ) : (
                <>
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  {saving ? 'Adding...' : 'Add to Cart'}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
